import type { WatchQueryFetchPolicy } from '@apollo/client/core/watchQueryOptions';
import type {
  FeaturedGuideWidgetQuery,
  FeaturedGuideWidgetQueryVariables,
  ProvisionedFeaturedGuidesQuery,
  ProvisionedFeaturedGuidesQueryVariables
} from '../../../../types/graphql-types';
import type { QueryResult } from '@apollo/client/react';
import featuredGuideWidgetQuery from './../FeaturedGuideWidget.query.graphql';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import { useContext } from 'react';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import type { FeaturedWidgetInstance } from '@aurora/shared-client/components/context/FeaturedWidgetContext/FeaturedWidgetContext';
import FeaturedWidgetContext from '@aurora/shared-client/components/context/FeaturedWidgetContext/FeaturedWidgetContext';
import provisionedGuidesQuery from './../ProvisionedFeaturedGuides.query.graphql';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import type { GuideEdge } from '@aurora/shared-generated/types/graphql-schema-types';

/**
 * Retrieves a list of selected guides based on the provided featured items IDs
 * and the guide query data. The returned list contains the properties of the
 * selected guides, maintaining the order of the featured items as specified
 * in the array.
 * @param guidesQueryData the provisioned guide query data
 * @param featuredItemIds list of selected featured item ids
 * @returns Array of featured guides with all their properties
 */
function getProvisionedFeaturedGuides(
  guidesQueryData: ProvisionedFeaturedGuidesQuery,
  featuredItemIds: string[]
): GuideEdge[] {
  const guides = guidesQueryData?.guides;
  if (guides != null) {
    const orderedGuideEdges: GuideEdge[] = featuredItemIds
      .map(id => guides.edges.find(guide => guide.node.id === id) as GuideEdge)
      .filter(guide => guide !== undefined);
    return orderedGuideEdges;
  }
  return [];
}

/**
 * Helper for using the FeaturedGuideWidget query
 *
 * @param queryVariables variables used by the FeaturedGuideWidget query
 * @param skip whether to skip the featured guide widget query
 * @param fetchPolicy the fetch policy for the query
 * @param onCompleted callback function invoked when
 */
export default function useFeaturedGuideWidget(
  queryVariables: FeaturedGuideWidgetQueryVariables,
  skip = false,
  fetchPolicy: WatchQueryFetchPolicy = 'cache-first',
  onCompleted?: (data: FeaturedGuideWidgetQuery | ProvisionedFeaturedGuidesQuery) => void
): QueryResult<FeaturedGuideWidgetQuery, FeaturedGuideWidgetQueryVariables> {
  const {
    publicConfig: { auroraFeaturedWidgetsEnabled }
  } = useContext(TenantContext);
  const { featuredWidgetInstances } = useContext(FeaturedWidgetContext);
  const { community } = useContext(AppContext);
  const { instanceId, useAvatar, first } = queryVariables;
  const sessionInstance: FeaturedWidgetInstance = featuredWidgetInstances?.find(
    featuredWidgetInstance => featuredWidgetInstance.instanceId === instanceId
  );
  const featuredItemIds = sessionInstance?.featuredItemIds;
  const sessionInstanceHasGuides: boolean = featuredItemIds?.length > 0;

  const skipQuery: boolean = skip || !auroraFeaturedWidgetsEnabled || !!sessionInstance;

  const skipProvisionalQuery: boolean =
    skip ||
    !auroraFeaturedWidgetsEnabled ||
    !sessionInstance ||
    // API throws an error when using the node id constraint and providing an empty array of ids, so skip
    (sessionInstance && !sessionInstanceHasGuides);

  // retrieves the guides and last modified information if the widget is already stored
  const featuredGuideWidgetQueryResult = useQueryWithTracing<
    FeaturedGuideWidgetQuery,
    FeaturedGuideWidgetQueryVariables
  >(module, featuredGuideWidgetQuery, {
    variables: queryVariables,
    skip: skipQuery,
    fetchPolicy,
    onCompleted
  });

  // if the widget has been added during the current page editor session queries for the selected guides directly
  const provisionedGuideQueryResult = useQueryWithTracing<
    ProvisionedFeaturedGuidesQuery,
    ProvisionedFeaturedGuidesQueryVariables
  >(module, provisionedGuidesQuery, {
    variables: {
      constraints: {
        nodeId: {
          eq: community.id
        },
        includeEmpty: {
          eq: true
        },
        includeHidden: {
          eq: true
        },
        includeGuideIds: {
          in: featuredItemIds
        }
      },
      first: featuredItemIds?.length,
      useAvatar: useAvatar
    },
    skip: skipProvisionalQuery,
    fetchPolicy,
    onCompleted: data => {
      const result: FeaturedGuideWidgetQuery = {
        featuredGuideWidget: {
          guides: {
            edges: getProvisionedFeaturedGuides(data, featuredItemIds).slice(0, first),
            totalCount: data?.guides?.totalCount || 0,
            pageInfo: data?.guides?.pageInfo
          }
        }
      };
      if (onCompleted) {
        onCompleted(result);
      }
    }
  });
  if (!sessionInstance) {
    return featuredGuideWidgetQueryResult;
  } else {
    // fallbacks provided in the event the widget was added during the current session but the query is skipped due
    //  to no guides yet being associated
    const { loading, error, data } = provisionedGuideQueryResult;
    return {
      ...featuredGuideWidgetQueryResult,
      loading: loading || false,
      error: error || null,
      data: {
        featuredGuideWidget: {
          guides: {
            edges: getProvisionedFeaturedGuides(data, featuredItemIds).slice(0, first),
            totalCount: data?.guides?.totalCount || 0,
            pageInfo: data?.guides?.pageInfo
              ? {
                  ...data?.guides?.pageInfo,
                  hasNextPage: first < data?.guides?.totalCount
                }
              : undefined
          }
        }
      }
    };
  }
}
